import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import {
  ArrowForward,
  Business,
  BusinessOutlined,
  LockOutlined,
  PeopleAltOutlined,
  PersonAddAlt1Outlined,
  Refresh,
  ScheduleOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { errorMessage } from '../../utils/errorMessage';
import CompanyAttentionList from './CompanyAttentionList';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import MetricCard from '../common/MetricCard';
import PageIconTile from '../common/PageIconTile';

const dateParam = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const SuperAdminOverview: React.FC = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const range = useMemo(() => { const end = new Date(); return { startDate: dateParam(new Date(end.getFullYear(), end.getMonth(), 1)), endDate: dateParam(end) }; }, []);
  const query = useQuery({ queryKey: ['admin', 'overview', range.startDate, range.endDate], queryFn: () => apiClient.getAdminOverview(range.startDate, range.endDate) });
  const refresh = async () => {
    await query.refetch();
    await queryClient.invalidateQueries({ queryKey: ['admin', 'companies', 'attention'] });
  };
  const metricCards = query.data ? [
    { label: t('dashboard.totalCompanies'), value: query.data.totalCompanyCount, icon: <BusinessOutlined />, color: theme.palette.primary.main },
    { label: t('dashboard.activeSubscriptionCompanies'), value: query.data.activeSubscriptionCompanyCount, icon: <VerifiedUserOutlined />, color: theme.palette.success.main },
    { label: t('dashboard.activeUsageCompanies'), value: query.data.activeUsageCompanyCount, icon: <PeopleAltOutlined />, color: theme.palette.info.main },
    { label: t('dashboard.newCompanies'), value: query.data.newCompanyCount, icon: <PersonAddAlt1Outlined />, color: theme.palette.secondary.main },
    { label: t('dashboard.expiringCompanies'), value: query.data.expiringIn7DaysCompanyCount, icon: <ScheduleOutlined />, color: theme.palette.warning.main },
    { label: t('dashboard.restrictedCompanies'), value: query.data.restrictedCompanyCount, icon: <LockOutlined />, color: theme.palette.error.main },
  ] : [];

  return <Box sx={{ flexGrow: 1, pb: { xs: 2, md: 4 } }}>
    <PageHeader
      title={t('dashboard.superAdminTitle')}
      description={t('dashboard.superAdminHint')}
      icon={<Business />}
      accent="dashboard"
      mobileMode="hidden"
      actions={<PageHeaderActionButton startIcon={<Refresh />} onClick={() => void refresh()} disabled={query.isFetching}>{t('dashboard.refresh')}</PageHeaderActionButton>}
    />
    {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error, t('dashboard.loadError'))}</Alert>}
    {query.isLoading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}><CircularProgress /></Box>}
    {query.data && <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mb: 1.5 }}>
        {metricCards.map(card => <MetricCard key={card.label} title={card.label} value={String(card.value)} icon={card.icon} color={card.color} />)}
      </Box>
      <CompanyAttentionList cacheKey={query.data.asOf} />
      <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, mt: 1.5, borderRadius: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <PageIconTile icon={<Business />} accent="companies" size={38} iconSize={21} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.companyManagement')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{t('dashboard.companyManagementHint')}</Typography>
            </Box>
          </Stack>
          <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/companies')}>{t('dashboard.viewCompanies')}</Button>
        </Stack>
      </Paper>
    </>}
  </Box>;
};

export default SuperAdminOverview;
